import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../api.js'
import { useAuth } from '../auth.jsx'
import { useConfig } from '../config.jsx'
import { Card, Field, Alert, UserSearch } from '../lib.jsx'

const ROLE_PL = { owner: 'właściciel', admin: 'administrator', member: 'członek' }

function MembersTab({ orgId }) {
  const [members, setMembers] = useState([])
  const [picked, setPicked] = useState(null)
  const [role, setRole] = useState('member')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  const load = () => api.orgMembers(orgId).then(setMembers).catch(e => setErr(e.message))
  useEffect(() => { load() }, [orgId])

  const add = async () => {
    if (!picked) return
    setBusy(true); setErr('')
    try { await api.addOrgMember(orgId, picked.id, role); setPicked(null); load() }
    catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  const changeRole = async (m, newRole) => {
    try { await api.updateOrgMemberRole(orgId, m.id, newRole); load() } catch (e) { setErr(e.message) }
  }
  const remove = async (m) => {
    if (!window.confirm('Usunąć tego członka z organizacji?')) return
    try { await api.removeOrgMember(orgId, m.id); load() } catch (e) { setErr(e.message) }
  }

  return (
    <Card title="Członkowie organizacji" desc="Wyszukaj istniejącego użytkownika systemu, aby dodać go bezpośrednio (bez zaproszenia e-mail).">
      <Alert kind="err">{err}</Alert>
      <div className="row" style={{ marginBottom: 18, alignItems: 'flex-start' }}>
        <div style={{ flex: 2, minWidth: 220 }}>
          {picked ? (
            <div className="unit-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span><b>{picked.username}</b> <span className="muted small">{picked.email}</span></span>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setPicked(null)}>Zmień</button>
            </div>
          ) : (
            <UserSearch onPick={setPicked} exclude={members.map(m => m.user_id)} />
          )}
        </div>
        <select value={role} onChange={e => setRole(e.target.value)} style={{ maxWidth: 160 }}>
          <option value="member">Członek</option>
          <option value="admin">Administrator</option>
        </select>
        <button className="btn btn-primary" disabled={busy || !picked} onClick={add}>Dodaj</button>
      </div>
      {members.length === 0 ? <div className="empty">Brak członków.</div> : (
        <table>
          <thead><tr><th>Użytkownik</th><th>Rola</th><th /></tr></thead>
          <tbody>
            {members.map(m => (
              <tr key={m.id}>
                <td>{m.username ? <><b>{m.username}</b> <span className="muted small">{m.email}</span></> : <span className="mono small">{m.user_id}</span>}</td>
                <td>
                  {m.role === 'owner' ? <span className="role-tag role-admin">{ROLE_PL.owner}</span> : (
                    <select value={m.role} onChange={e => changeRole(m, e.target.value)}>
                      <option value="member">{ROLE_PL.member}</option>
                      <option value="admin">{ROLE_PL.admin}</option>
                    </select>
                  )}
                </td>
                <td>{m.role !== 'owner' && <button className="btn btn-sm btn-danger" onClick={() => remove(m)}>Usuń</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  )
}

function InviteTab({ orgId }) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('member')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [link, setLink] = useState('')

  const submit = async (e) => {
    e.preventDefault()
    setErr(''); setBusy(true); setLink('')
    try {
      const invite = await api.inviteOrgMember(orgId, { email, role })
      setLink(`${location.origin}/accept-invite?token=${invite.token}`)
      setEmail('')
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  return (
    <Card title="Zaproś do organizacji" desc="Zaproszenie e-mail zostało już wysłane przez backend — poniższy link możesz też przekazać ręcznie.">
      <form onSubmit={submit}>
        <Alert kind="err">{err}</Alert>
        <div className="row">
          <Field label="E-mail"><input type="email" value={email} onChange={e => setEmail(e.target.value)} required /></Field>
          <Field label="Rola">
            <select value={role} onChange={e => setRole(e.target.value)}>
              <option value="member">Członek</option>
              <option value="admin">Administrator</option>
            </select>
          </Field>
        </div>
        <button className="btn btn-primary" disabled={busy || !email}>{busy ? 'Wysyłanie…' : 'Zaproś'}</button>
      </form>
      {link && (
        <div className="field" style={{ marginTop: 18 }}>
          <label>Link zaproszenia (do ręcznego przekazania)</label>
          <input readOnly value={link} onClick={e => e.target.select()} />
        </div>
      )}
    </Card>
  )
}

export default function OrganizationDetail() {
  const { id } = useParams()
  const nav = useNavigate()
  const { user } = useAuth()
  const cfg = useConfig()
  const [org, setOrg] = useState(null)
  const [err, setErr] = useState('')
  const [delBusy, setDelBusy] = useState(false)

  useEffect(() => { api.organization(id).then(setOrg).catch(e => setErr(e.message)) }, [id])

  const isOwner = org && user && org.owner_user_id === user.id

  const remove = async () => {
    if (!window.confirm(`Usunąć organizację „${org.name}” bezpowrotnie? Tej operacji nie można cofnąć.`)) return
    setDelBusy(true)
    try { await api.deleteOrganization(id); nav('/organizations') } catch (e) { setErr(e.message); setDelBusy(false) }
  }

  if (err && !org) return <div className="content"><Alert kind="err">{err}</Alert></div>
  if (!org) return <div className="content"><div className="empty">Wczytywanie…</div></div>

  return (
    <>
      <div className="topbar">
        <div>
          <button className="btn btn-sm btn-ghost" style={{ marginBottom: 10 }} onClick={() => nav('/organizations')}>‹ Organizacje</button>
          <h1>{org.name}</h1>
          <div className="sub">{org.slug}</div>
        </div>
        {isOwner && <button className="btn btn-danger" disabled={delBusy} onClick={remove}>{delBusy ? 'Usuwanie…' : 'Usuń organizację'}</button>}
      </div>
      <div className="content">
        <Alert kind="err">{err}</Alert>
        <div className="grid" style={{ gap: 18 }}>
          <MembersTab orgId={id} />
          {cfg.features.org_invites && <InviteTab orgId={id} />}
        </div>
      </div>
    </>
  )
}
